import { api } from '../api/client';
import type { FxRate, ListingModel, SkinGroup, TemplateModel } from '../api/types';
import type { CartLine } from '../hooks/useCart';

/**
 * Uma peça anunciada, pronta para entrar na sacola.
 *
 * A lista de anúncios e o kit põem peças na sacola do mesmo jeito; a linha é
 * montada num lugar só para as duas não guardarem campos diferentes.
 */
export function linhaDoAnuncio(
  listing: ListingModel,
  opts: {
    rate: FxRate | null;
    exterior: string | null;
    iconReserva?: string | null;
  },
): Omit<CartLine, 'qty'> {
  return {
    listingId: listing.commodityNo,
    templateId: listing.templateId ?? 0,
    name: listing.name ?? 'Skin',
    iconUrl: listing.iconUrl ?? opts.iconReserva ?? null,
    exterior: opts.exterior,
    float: listing.float,
    paintSeed: listing.paintSeed,
    // O link de inspeção segue a peça: o carrinho e o Upgrade precisam dele, e
    // o anúncio pode sair da listagem depois.
    inspectUrl: listing.inspectUrl,
    imageUrl: listing.imageUrl,
    // A sacola guarda em reais mesmo com o site em ¥: o pedido é fechado em reais.
    unitBrl: listing.price !== null && opts.rate ? listing.price * opts.rate.rate : 0,
  };
}

/** O desgaste escolhido no kit, ou o mais barato à venda quando não há escolha. */
function templateDaPeca(grupo: SkinGroup, exterior: string | null): TemplateModel | null {
  const comPreco = grupo.variants.filter((v) => v.price !== null);
  if (exterior) return comPreco.find((v) => v.exterior?.code === exterior) ?? null;
  return comPreco.reduce<TemplateModel | null>((m, v) => (m === null || v.price! < m.price! ? v : m), null);
}

/**
 * Escolhe a peça mais barata à venda de cada skin do kit.
 *
 * O kit guarda a skin e o desgaste, não uma peça: é o que o mantém de pé quando
 * um anúncio é vendido. Para ir à sacola, alguém precisa escolher a peça, e o
 * padrão é a mais barata — quem quer um float específico troca depois, pela
 * página da skin.
 *
 * Lê uma página só, ordenada por preço, em vez de varrer a skin inteira: é o
 * que deixa o botão responder em segundos.
 */
export async function pecasDoKit(
  pecas: { grupo: SkinGroup; exterior: string | null }[],
): Promise<{ template: TemplateModel; anuncio: ListingModel }[]> {
  return Promise.all(
    pecas.map(async ({ grupo, exterior }) => {
      const template = templateDaPeca(grupo, exterior);
      if (!template) throw new Error(`${grupo.name} não tem oferta nesse desgaste agora.`);
      const r = await api.listings({
        templateId: template.id,
        sort: 'price_asc',
        pageSize: 20,
        scan: false,
      });
      const anuncio = r.items
        .filter((l) => l.price !== null)
        .reduce<ListingModel | null>((m, l) => (m === null || l.price! < m.price! ? l : m), null);
      if (!anuncio) throw new Error(`${grupo.name} ficou sem anúncio agora. Tente outro desgaste.`);
      return { template, anuncio };
    }),
  );
}
